<template>
    <div>
        <div v-if="isLoading" class="has-text-centered p-5">
            <e-loading :is-full-page="false" :active="isLoading" />
        </div>
        <div v-else class="tw:grid tw:gap-2">
            <div v-if="creationFailed">
                <div class="notification is-warning">
                    <p><strong>Note Creation Failed</strong></p>
                    <p class="is-size-7 mt-2">
                        Unable to create note initially. Don't worry - your content will be saved when you close the
                        drawer or click save. File uploads are temporarily unavailable.
                    </p>
                </div>
            </div>
            <div>
                <e-field label="Title">
                    <e-input
                        v-model="localTitle"
                        type="input"
                        class="tw:w-full"
                        @input="handleTitleInput"
                    />
                </e-field>
            </div>
            <div>
                <e-field label="Course">
                    <course-select
                        :already-selected="selectedCourses"
                        :limit="1"
                        @save="handleCourseSave"
                        @remove="handleCourseRemove"
                    />
                </e-field>
            </div>
            <div>
                <e-field label="Tags">
                    <tag-switcher
                        :key="key"
                        :tags="assignableTags"
                        :subject="noteModel"
                        action="NoteDrawer/updateTags"
                        :tag-type="noteTagType"
                        uses-tag-type
                        @sync-tags="handleSyncTags"
                    />
                </e-field>
            </div>
            <div>
                <e-field>
                    <rich-text-editor
                        :key="key"
                        v-model="localBody"
                        compact-toolbar
                        @input="handleBodyInput"
                    />
                </e-field>
            </div>
            <div>
                <edverum-file-upload
                    :key="localMedia ? localMedia.length : key"
                    :autosave="false"
                    :attachments="localMedia"
                    @add="addAttachment"
                    @delete="removeAttachment"
                />
            </div>

            <div>
                <e-button v-if="noteId" type="is-danger" outlined @click="deleteNote(noteId)">
                    <e-icon icon="pi pi-trash" />
                </e-button>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import isEmpty from 'lodash/isEmpty.js';
import RichTextEditor from '@/components/editor/Editor.vue';
import EdverumFileUpload from '@/components/file-upload/EdverumFileUpload.vue';
import CourseSelect from '@/components/modal-select/CourseSelect.vue';
import TagSwitcher from '@/components/switcher/TagSwitcher.vue';

export default {
    name: 'NoteForm',

    components: {
        RichTextEditor,
        EdverumFileUpload,
        TagSwitcher,
        CourseSelect,
    },

    emits: ['note-saved', 'form-ready'],

    props: {
        note: {
            type: Object,
            required: true,
        },
        namespace: {
            type: String,
            required: true,
        },
        edit: {
            type: Boolean,
            default: false,
        },
        courseId: {
            type: Number,
            default: null,
        },
        activityId: {
            type: Number,
            default: null,
        },
        tempNote: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            localTitle: '',
            localBody: '',
            localMedia: [],
            localTags: [],
            noteId: null,
            isNewNote: false,
            selectedCourses: [],
            isLoading: false,
            isShowForm: false,
            key: 0,
            lastSavedSnapshot: null, // Track last saved state to detect unsaved changes
            creationFailed: false, // Track if initial note creation failed
            tagsDirty: false, // Track explicit tag edits to avoid stale overwrite on full save
        };
    },

    computed: {
        assignableTags() {
            // Only show tags already assigned to the note
            return this.localTags || [];
        },

        userId() {
            return this.$store.getters['Auth/userId'];
        },

        noteTagType() {
            return `notes-${this.userId}`;
        },

        noteModel() {
            return {
                id: this.noteId,
                tags: this.localTags || [],
                courseId: this.courseId,
            };
        },

        title: {
            get() {
                return this.get('title');
            },
            set(value) {
                this.set('title', value);
            },
        },
    },

    watch: {
        note: {
            immediate: true,
            handler(newNote) {
                if (newNote && newNote.id) {
                    // Process tags to handle both object and string formats
                    const processedTags = newNote.tags
                        ? newNote.tags.map(tag => {
                              if (typeof tag === 'string') {
                                  return {
                                      id: null,
                                      name: { en: tag },
                                      type: this.noteTagType,
                                  };
                              }
                              return tag;
                          })
                        : [];

                    this.localTags = processedTags;
                    this.tagsDirty = false;
                } else {
                    this.localTags = [];
                    this.tagsDirty = false;
                }
            },
        },
    },

    async created() {
        this.isLoading = true;
        this.isInitializing = true;

        this.localTitle = '';
        this.localBody = '';
        this.localMedia = [];
        this.localTags = [];
        this.noteId = null;
        this.isNewNote = false;
        this.selectedCourses = this.note.course ? [this.note.course] : [];

        if (this.note && this.note.id) {
            await this.initializeNoteData(this.note, false);
        } else {
            // For new notes, create immediately so we have an ID for file uploads
            try {
                await this.createEmptyNote();
                this.creationFailed = false; // Clear flag on success
            } catch (error) {
                this.creationFailed = true; // Mark creation as failed
                // Show persistent error to user
                this.$edverumToast.open({
                    message: 'Note creation failed. Your changes will be saved when you close the drawer.',
                    type: 'is-warning',
                    duration: 5000,
                    position: 'is-top',
                });
            }
        }

        if (this.courseId) {
            this.set('course_id', this.courseId);
            await this.fetchCourse(this.courseId);
        }
        if (this.activityId) {
            this.set('activity_id', this.activityId);
        }

        this.isLoading = false;
        this.isInitializing = false;
    },

    mounted() {
        setTimeout(() => {
            this.isInitialLoad = false;
        }, 1000);
    },

    // Note: beforeUnmount removed - Vue 3 doesn't await async lifecycle hooks
    // Parent components should call saveIfNeeded() explicitly before closing

    methods: {
        isEmpty,

        handleTitleInput(event) {
            this.localTitle = event.target.value;
        },

        handleTitleBlur() {
            const trimmedTitle = this.localTitle?.trim() || '';
            if (trimmedTitle !== this.localTitle) {
                this.localTitle = trimmedTitle;
            }
        },

        handleBodyInput(content) {
            this.localBody = content || '';
        },

        handleBodyBlur() {
            // Body blur no longer triggers save
        },

        handleSyncTags(tags) {
            this.localTags = Array.isArray(tags) ? JSON.parse(JSON.stringify(tags)) : [];
            this.tagsDirty = true;
        },

        async saveNote() {
            // Check if note is completely empty (only title, body, media, and tags matter for "content")
            const hasContent = !!(
                (this.localTitle && this.localTitle.trim()) ||
                (this.localBody && this.localBody.trim()) ||
                (this.localMedia && this.localMedia.length > 0) ||
                (this.localTags && this.localTags.length > 0)
            );

            // If completely empty, delete the note and allow closing
            if (!hasContent) {
                if (this.noteId) {
                    try {
                        await axios.delete(`/api/notes/${this.noteId}`);
                        const Note = (await import('@/models/Note.js')).default;
                        Note.delete(this.noteId);

                        // Clear component state after successful deletion
                        this.noteId = null;
                        this.updateLastSavedValues(); // Update snapshot to reflect empty state
                    } catch {
                        // Deletion failed silently - note may already be deleted or network error
                    }
                }
                // Return special flag to indicate empty note (no validation error)
                return { isEmpty: true };
            }

            // If has content, title is required
            if (!this.localTitle || !this.localTitle.trim()) {
                this.$edverumToast.open({
                    message: 'Title is required',
                    type: 'is-danger',
                });
                throw new Error('Title is required');
            }

            const wasNewNote = !this.noteId;
            try {
                // Always map tags to names for backend
                const tagNames = (this.localTags || []).map(tag => (tag && tag.name ? tag.name.en || tag.name : tag));

                // Prepare note data
                const noteData = {
                    title: this.localTitle.trim(),
                    body: this.localBody,
                    media: this.localMedia,
                    course_id: this.selectedCourses[0]?.id || null, // Use null to clear course
                    activity_id: this.activityId || this.note.activity_id,
                };

                // Only include tags for new notes or when user explicitly changed tags.
                // This prevents stale local tag state from overwriting autosaved tags.
                if (!this.noteId || this.tagsDirty) {
                    noteData.tags = tagNames;
                }

                // Guard: If noteId is missing (createEmptyNote failed), create the note first
                if (!this.noteId) {
                    const newNote = await this.$store.dispatch('NoteDrawer/createNote', noteData);
                    if (newNote && newNote.id) {
                        this.noteId = newNote.id;
                        this.isNewNote = false;
                        this.creationFailed = false; // Clear failure flag on successful creation
                    } else {
                        throw new Error('Failed to create note');
                    }
                } else {
                    // Update existing note using store action
                    const result = await this.$store.dispatch('NoteDrawer/updateNote', {
                        id: this.noteId,
                        data: noteData,
                    });
                }

                this.lastSavedSnapshot = JSON.stringify({
                    title: this.localTitle,
                    body: this.localBody,
                    media: this.localMedia,
                    tags: tagNames,
                });
                this.tagsDirty = false;

                this.$edverumToast.open('Note saved successfully');

                if (this.isNewNote) {
                    this.isNewNote = false;
                }

                // Force refresh the specific note in ORM to ensure table shows updated data
                try {
                    const Note = (await import('@/models/Note.js')).default;
                    const Taggable = (await import('@/models/Taggable.js')).default;
                    const Tag = (await import('@/models/Tag.js')).default;

                    // More thorough cleanup of ORM cache
                    Note.delete(this.noteId);

                    // Clear all taggables for this note
                    Taggable.query().where('taggable_id', this.noteId).delete();

                    // Clear any cached tags that might be related to this note
                    const existingNote = Note.query().where('id', this.noteId).first();
                    if (existingNote && existingNote.tags) {
                        existingNote.tags.forEach(tag => {
                            Tag.delete(tag.id);
                        });
                    }

                    // Use direct axios instead of Vuex ORM API call
                    const axiosInstance = (await import('axios')).default;
                    const freshResponse = await axiosInstance.get(
                        `/api/notes/${this.noteId}?include=tags,media,activity,course`
                    );

                    // Insert the fresh data into ORM
                    if (freshResponse.data?.data) {
                        Note.insert({ data: freshResponse.data.data });

                        // Verify the ORM data
                        const ormNote = Note.query().with('tags').where('id', this.noteId).first();
                    }
                } catch {
                    // ORM cache refresh failed - table may show stale data until next refresh
                }

                await this.$store.dispatch('NoteDrawer/noteSaved');
                this.$emit('note-saved', { isNew: wasNewNote, noteId: this.noteId });

                // Update snapshot after successful save to prevent double-save
                this.updateLastSavedValues();

                // Clear table apiResponse so it falls back to fresh data prop
                this.$nextTick(() => {
                    this.clearTableApiResponse();
                });

                return { success: true };
            } catch (error) {
                console.error('[NoteForm] saveNote - error:', error);
                this.$edverumToast.open({
                    message: 'Error saving note. Please try again.',
                    type: 'is-danger',
                });
                throw error;
            }
        },

        /**
         * Save the note if there are unsaved changes.
         * Exposed for parent components to call explicitly before closing/unmounting.
         * Returns a promise that resolves with the saveNote result if save was needed,
         * or undefined if no save was needed.
         */
        async saveIfNeeded() {
            if (this.hasUnsavedChanges()) {
                return this.saveNote();
            }
            // No unsaved changes, return undefined
            return undefined;
        },

        clear() {
            this.localTitle = null;
            this.localBody = null;
            this.localMedia = null;
            this.localTags = null;
            this.key += 1;
        },

        addAttachment(media) {
            this.localMedia = media;
        },

        async removeAttachment(index, item) {
            try {
                await this.$store.dispatch('NoteDrawer/deleteMedia', {
                    media: item,
                });
            } catch (error) {
                // Optionally show user-friendly error message
            }
        },

        showForm() {
            this.isShowForm = !this.isShowForm;
        },

        get(field) {
            return this.$store.getters[`${this.namespace}/get`](field);
        },

        set(field, value) {
            this.$store.dispatch('NoteDrawer/editField', { field, value });
        },

        async deleteNote(noteId) {
            await this.$dialog.confirm({
                title: 'Removing Notes',
                message: `Are you sure you want to DELETE  <strong>${this.localTitle}</strong>?`,
                confirmText: 'Delete',
                type: 'is-danger',
                onConfirm: async () => {
                    await axios.delete(`/api/notes/${noteId}`);
                    const Note = (await import('@/models/Note.js')).default;
                    Note.delete(noteId);
                    this.$edverumToast.open('Note has been removed.');
                    await this.$store.commit('NoteDrawer/CLOSE_NOTE_DRAWER');
                    this.$store.commit('NoteTags/UPDATE_REFRESH_KEY', this.courseId);
                },
            });
        },

        async fetchCourse(courseId) {
            // Currently disabled - return early
        },

        handleCourseSave(course) {
            this.selectedCourses = [course[0]];
        },

        handleCourseRemove() {
            this.selectedCourses = [];
        },

        resetForm() {
            this.localTitle = '';
            this.localBody = '';
            this.localMedia = [];
            this.localTags = [];
            this.noteId = null;
            this.isNewNote = false;
            this.selectedCourses = [];
            this.isInitialized = false;
        },

        async initializeNoteData(note, shouldUpdateEditor = true) {
            this.isInitializing = true;
            try {
                await this.setLocalState(note);
                await this.setStoreValues(note);
                await this.handleCourseSelection(note);
                this.handleFormReady(shouldUpdateEditor, note);
                // Initialize snapshot after loading existing note
                this.updateLastSavedValues();
            } catch (error) {
                this.$store.commit('NoteDrawer/SET_LOADING', false);
                this.notifyFormReady();
            } finally {
                this.isInitializing = false;
            }
        },

        setLocalState(note) {
            const title = this.parseNoteTitle(note.title);
            this.localTitle = title;
            this.localBody = note.body || '';
            this.localMedia = note.media || [];
            this.localTags = note.tags || [];
            this.noteId = note.id || null;
            this.isNewNote = !note.id;
            this.isInitialized = true;
            this.tagsDirty = false;
        },

        parseNoteTitle(title) {
            if (typeof title === 'string' && title.startsWith('{')) {
                return JSON.parse(title).title || '';
            }
            return title || '';
        },

        initializeBasicState(note, title) {
            this.localTitle = title;
            this.localBody = note.body || '';
            this.localMedia = note.media || [];
            this.localTags = note.tags || [];
            this.noteId = note.id || null;
            this.isNewNote = !note.id;
            this.isInitialized = true;
        },

        initializeCourseState(note) {
            if (note.course) {
                this.selectedCourses = [note.course];
            } else if (note.course_id) {
                this.fetchCourse(note.course_id);
            }
        },

        setStoreValues(note) {
            this.$store.dispatch('NoteDrawer/editField', { field: 'title', value: this.localTitle });
            this.$store.dispatch('NoteDrawer/editField', { field: 'body', value: note.body || '' });
            this.$store.dispatch('NoteDrawer/editField', { field: 'id', value: note.id });
            this.$store.dispatch('NoteDrawer/editField', { field: 'course_id', value: note.course_id });
            this.$store.dispatch('NoteDrawer/editField', { field: 'isNewNote', value: !note.id });
            this.$store.dispatch('NoteDrawer/editField', { field: 'media', value: note.media || [] });
            this.$store.dispatch('NoteDrawer/editField', { field: 'tags', value: note.tags || [] });
        },

        async handleCourseSelection(note) {
            if (note.course) {
                this.selectedCourses = [note.course];
            } else if (note.course_id) {
                await this.fetchCourse(note.course_id);
            }
        },

        handleFormReady() {
            this.$store.commit('NoteDrawer/SET_LOADING', false);
            this.notifyFormReady();
        },

        notifyFormReady() {
            this.isLoading = false;
            this.$emit('form-ready');
        },

        debouncedSave(field, value) {
            if (this.saveTimeout) {
                clearTimeout(this.saveTimeout);
            }

            this.saveTimeout = setTimeout(() => {
                this.set(field, value);
            }, 1000);
        },

        clearTableApiResponse() {
            // Look for parent table components and clear their apiResponse
            let parent = this.$parent;
            while (parent) {
                if (parent.$refs && parent.$refs.notesTable) {
                    const table = parent.$refs.notesTable;
                    if (table.apiResponse && table.model?.entity === 'notes') {
                        table.apiResponse = null;
                    }
                    break;
                }
                parent = parent.$parent;
            }
        },

        async createEmptyNote() {
            try {
                const noteData = {
                    title: '',
                    body: '',
                    media: [],
                    tags: [],
                    course_id: this.courseId || this.note.course_id,
                    activity_id: this.activityId || this.note.activity_id,
                };

                const newNote = await this.$store.dispatch('NoteDrawer/createNote', noteData);
                const savedNoteId = newNote?.id;

                if (savedNoteId) {
                    this.noteId = savedNoteId;
                    this.localTitle = '';
                    this.isNewNote = false;
                    this.creationFailed = false; // Clear failure flag on success
                    // Initialize snapshot for new empty note
                    this.updateLastSavedValues();
                } else {
                    // Store action returned but no ID - unexpected state
                    throw new Error('Note creation returned no ID');
                }
            } catch (error) {
                this.creationFailed = true; // Mark creation as failed
                // Re-throw so callers can handle UI messaging
                throw error;
            }
        },

        hasUnsavedChanges() {
            // Create current snapshot
            const tagNames = (this.localTags || []).map(tag => (tag && tag.name ? tag.name.en || tag.name : tag));
            const courseId = this.selectedCourses[0]?.id || null;
            const currentSnapshot = JSON.stringify({
                title: this.localTitle || '',
                body: this.localBody || '',
                media: this.localMedia || [],
                tags: tagNames,
                course_id: courseId,
            });

            // If we have a saved snapshot, compare against it
            if (this.lastSavedSnapshot) {
                return currentSnapshot !== this.lastSavedSnapshot;
            }

            // No snapshot exists - use fallback comparison
            // If original note data exists, compare against it
            if (this.note && this.note.id) {
                const originalTagNames = (this.note.tags || []).map(tag =>
                    tag && tag.name ? tag.name.en || tag.name : tag
                );
                const originalSnapshot = JSON.stringify({
                    title: this.note.title || '',
                    body: this.note.body || '',
                    media: this.note.media || [],
                    tags: originalTagNames,
                    course_id: this.note.course_id || null,
                });
                return currentSnapshot !== originalSnapshot;
            }

            // No snapshot and no original note - treat any non-empty content as unsaved
            // IMPORTANT: If creation failed, we must treat any content as unsaved
            const hasContent = !!(
                (this.localTitle && this.localTitle.trim()) ||
                (this.localBody && this.localBody.trim()) ||
                (this.localMedia && this.localMedia.length > 0) ||
                (this.localTags && this.localTags.length > 0) ||
                (this.selectedCourses && this.selectedCourses.length > 0)
            );

            // If creation failed and there's content, always treat as unsaved
            if (this.creationFailed && hasContent) {
                return true;
            }

            return hasContent;
        },

        updateLastSavedValues() {
            // Update snapshot after successful save
            const tagNames = (this.localTags || []).map(tag => (tag && tag.name ? tag.name.en || tag.name : tag));
            const courseId = this.selectedCourses[0]?.id || null;
            this.lastSavedSnapshot = JSON.stringify({
                title: this.localTitle || '',
                body: this.localBody || '',
                media: this.localMedia || [],
                tags: tagNames,
                course_id: courseId,
            });
        },
    },
};
</script>
